import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { MdClose } from 'react-icons/md';
import ModelViewer from './ModelViewer';
import type { DisplayMode } from './ModelViewer';
import { DEFAULT_DISPLAY_MODE } from './ModelViewer.constants';
import { usePortalContainer, useViewerEnvironment } from './environment';
import type { ModelLoaderType } from './modelFiles';
import ModelViewerProvider from './ModelViewerProvider';
import ViewerErrorBoundary from './ViewerErrorBoundary';
import ViewerStatus from './chrome/ViewerStatus';
import { messages } from './chrome/messages';
import { useCoarsePointer } from './chrome/useCoarsePointer';
import {
  ModelViewerModalOverlay,
  ModelViewerModalHeader,
  ModelViewerModalContent,
  ModelViewerModalClose,
  ModelViewerHint,
} from './ModelViewerModal.styled';

interface ModelViewerModalProps {
  data: Blob;
  fileName: string;
  loaderType: ModelLoaderType;
  /** The shading the model was already being looked at with. */
  initialDisplayMode?: DisplayMode;
  onClose: () => void;
}

const ModelViewerModal: React.FC<ModelViewerModalProps> = ({
  data,
  fileName,
  loaderType,
  initialDisplayMode = DEFAULT_DISPLAY_MODE,
  onClose,
}) => {
  const intl = useIntl();
  const coarse = useCoarsePointer();
  const environment = useViewerEnvironment();
  const container = usePortalContainer();
  const [attempt, setAttempt] = useState(0);
  // The viewer's menus and tooltips open inside the dialog: portaled outside it, they sit beneath the
  // modal, and assistive technology hides them while the dialog is open.
  const [layer, setLayer] = useState<HTMLDivElement | null>(null);

  // An Esc the viewer used (closing a menu, ending a tool) is marked handled.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    // Inside a shadow root the document only knows the host, so ask the root what had focus.
    const root = container.getRootNode() as Document | ShadowRoot;
    const previous = root.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previous?.focus?.();
  }, [container]);

  return createPortal(
    <ModelViewerModalOverlay onClick={onClose} role="presentation" data-testid="model-viewer-modal__backdrop">
      <ModelViewerModalHeader onClick={(e) => e.stopPropagation()}>
        <ModelViewerModalClose ref={closeRef} onClick={onClose} aria-label={intl.formatMessage(messages.closeViewer)}>
          <MdClose />
        </ModelViewerModalClose>
      </ModelViewerModalHeader>
      <ModelViewerModalContent
        role="dialog"
        aria-modal="true"
        aria-label={fileName}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <ViewerErrorBoundary
          key={attempt}
          fileName={fileName}
          fallback={<ViewerStatus status="failed" onRetry={() => setAttempt((n) => n + 1)} />}
        >
          <ModelViewerProvider {...environment} portalContainer={layer ?? environment.portalContainer}>
            <ModelViewer
              data={data}
              fileName={fileName}
              loaderType={loaderType}
              displayMode={initialDisplayMode}
            />
          </ModelViewerProvider>
        </ViewerErrorBoundary>
        <div ref={setLayer} />
      </ModelViewerModalContent>
      <ModelViewerHint>{intl.formatMessage(coarse ? messages.hintTouch : messages.hintMouse)}</ModelViewerHint>
    </ModelViewerModalOverlay>,
    container,
  );
};

export default ModelViewerModal;
